import React, { useEffect, useMemo, useState } from 'react'
import { api } from '../lib/api'
import MenuGuideButton from '../components/MenuGuideButton'
import Loading from '../ui/Loading'
import { useToast } from '../ui/ToastProvider'

const RENDER_MODES = [
  { value: 'HTML', label: 'HTML' },
  { value: 'PNG_OVERLAY', label: 'PNG 오버레이' },
  { value: 'HYBRID', label: '혼합' },
]

const TEMPLATE_STATUS_OPTIONS = [
  { value: 'DRAFT', label: '작성중' },
  { value: 'PUBLISHED', label: '게시' },
  { value: 'ARCHIVED', label: '보관' },
]

const FIELD_TYPE_LABELS = {
  TEXT: '텍스트',
  IMAGE: '이미지',
  RECT: '영역',
  SECTION: '섹션',
}

const DEFAULT_STYLE_DEFAULTS = {
  fontFamily: 'NotoSansKR',
  fontSize: 24,
  fontWeight: '700',
  textColor: '#111827',
  textAlign: 'LEFT',
  lineHeight: 1.2,
}

const DEFAULT_FONT_OPTIONS = [
  { fontKey: 'NotoSansKR', displayName: 'Noto Sans KR', systemFont: true, availableWeights: [400, 700, 900] },
  { fontKey: 'NanumGothic', displayName: '나눔고딕', systemFont: true, availableWeights: [400, 700, 900] },
]

const DEFAULT_FONT_WEIGHTS = [400, 700, 900]

const FONT_WEIGHT_LABELS = {
  100: 'Thin',
  200: 'ExtraLight',
  300: 'Light',
  400: 'Regular',
  500: 'Medium',
  600: 'SemiBold',
  700: 'Bold',
  800: 'ExtraBold',
  900: 'Black',
}

function unwrap(res) {
  return res?.data?.data !== undefined ? res.data.data : res?.data
}

function filenameFromContentDisposition(value, fallback) {
  const cd = String(value || '')
  const encoded = cd.match(/filename\*=UTF-8''([^;]+)/i)?.[1]
  if (encoded) {
    try {
      return decodeURIComponent(encoded)
    } catch {
      return encoded
    }
  }
  const quoted = cd.match(/filename="([^"]+)"/i)?.[1]
  return quoted || fallback
}

function downloadBlob(blob, fileName) {
  const url = window.URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = fileName
  document.body.appendChild(link)
  link.click()
  link.remove()
  window.setTimeout(() => window.URL.revokeObjectURL(url), 1000)
}

function toNumber(value, fallback = 0) {
  const n = Number(value)
  return Number.isFinite(n) ? n : fallback
}

function parseTemplateConfig(configJson) {
  const raw = String(configJson || '').trim()
  if (!raw) return {}
  try {
    const parsed = JSON.parse(raw)
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {}
  } catch {
    return {}
  }
}

function normalizeStyleDefaults(raw) {
  const source = raw && typeof raw === 'object' ? raw : {}
  return {
    fontFamily: source.fontFamily || DEFAULT_STYLE_DEFAULTS.fontFamily,
    fontSize: toNumber(source.fontSize, DEFAULT_STYLE_DEFAULTS.fontSize),
    fontWeight: source.fontWeight || DEFAULT_STYLE_DEFAULTS.fontWeight,
    textColor: /^#[0-9a-fA-F]{6}$/.test(String(source.textColor || '')) ? source.textColor : DEFAULT_STYLE_DEFAULTS.textColor,
    textAlign: ['LEFT', 'CENTER', 'RIGHT'].includes(source.textAlign) ? source.textAlign : DEFAULT_STYLE_DEFAULTS.textAlign,
    lineHeight: toNumber(source.lineHeight, DEFAULT_STYLE_DEFAULTS.lineHeight),
  }
}

function fontOptionKey(option) {
  return String(option?.fontKey || option?.value || '').trim()
}

function fontOptionLabel(option) {
  return option?.displayName || option?.label || fontOptionKey(option)
}

function normalizeFontWeight(value) {
  const n = Number(value)
  if (!Number.isFinite(n)) return ''
  const rounded = Math.round(n / 100) * 100
  if (rounded < 100 || rounded > 900) return ''
  return String(rounded)
}

function fontWeightLabel(value) {
  const weight = normalizeFontWeight(value) || String(value || '')
  return `${weight} ${FONT_WEIGHT_LABELS[weight] || ''}`.trim()
}

function fontWeightsForOption(option, selectedWeight) {
  const set = new Set()
  const source = Array.isArray(option?.availableWeights) && option.availableWeights.length
    ? option.availableWeights
    : DEFAULT_FONT_WEIGHTS
  source.forEach((weight) => {
    const normalized = normalizeFontWeight(weight)
    if (normalized) set.add(normalized)
  })
  const selected = normalizeFontWeight(selectedWeight)
  if (selected) set.add(selected)
  return Array.from(set).sort((a, b) => Number(a) - Number(b))
}

function findFontOption(options, fontKey) {
  const key = String(fontKey || '').trim()
  return (Array.isArray(options) ? options : []).find((option) => fontOptionKey(option) === key) || null
}

function closestFontWeight(option, preferredWeight) {
  const weights = fontWeightsForOption(option, preferredWeight)
  const preferred = Number(normalizeFontWeight(preferredWeight) || DEFAULT_STYLE_DEFAULTS.fontWeight)
  return weights.reduce((best, weight) => (
    Math.abs(Number(weight) - preferred) < Math.abs(Number(best) - preferred) ? weight : best
  ), weights[0] || DEFAULT_STYLE_DEFAULTS.fontWeight)
}

function mergeFontOptions(options, selectedValues = []) {
  const map = new Map()
  DEFAULT_FONT_OPTIONS.forEach((option) => map.set(option.fontKey, option))
  ;(Array.isArray(options) ? options : []).forEach((option) => {
    const key = fontOptionKey(option)
    if (key) map.set(key, option)
  })
  selectedValues.forEach((value) => {
    const key = String(value || '').trim()
    if (key && !map.has(key)) {
      map.set(key, { fontKey: key, displayName: key })
    }
  })
  return Array.from(map.values())
}

function buildTemplateConfigJson(configJson, styleDefaults, patch = {}) {
  const base = parseTemplateConfig(configJson)
  return JSON.stringify({
    ...base,
    ...patch,
    styleDefaults: normalizeStyleDefaults(styleDefaults),
  }, null, 2)
}

function effectiveFieldStyle(field, styleDefaults) {
  if (field?.useTemplateStyle) return styleDefaults
  return {
    fontFamily: field?.fontFamily || styleDefaults.fontFamily,
    fontSize: toNumber(field?.fontSize, styleDefaults.fontSize),
    fontWeight: field?.fontWeight || styleDefaults.fontWeight,
    textColor: field?.textColor || styleDefaults.textColor,
    textAlign: field?.textAlign || styleDefaults.textAlign,
    lineHeight: toNumber(field?.lineHeight, styleDefaults.lineHeight),
  }
}

function statusBadgeClass(status) {
  if (status === 'PUBLISHED') return 'bg-emerald-50 text-emerald-700 ring-emerald-100'
  if (status === 'ARCHIVED') return 'bg-slate-100 text-slate-600 ring-slate-200'
  return 'bg-amber-50 text-amber-700 ring-amber-100'
}

function approvalBadgeClass(status) {
  if (status === 'APPROVED') return 'bg-emerald-50 text-emerald-700 ring-emerald-100'
  if (status === 'REQUESTED') return 'bg-sky-50 text-sky-700 ring-sky-100'
  if (status === 'REJECTED') return 'bg-rose-50 text-rose-700 ring-rose-100'
  return 'bg-slate-100 text-slate-600 ring-slate-200'
}

function formatDateTime(value) {
  if (!value) return '-'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return String(value)
  return new Intl.DateTimeFormat('ko-KR', {
    dateStyle: 'short',
    timeStyle: 'short',
  }).format(date)
}

function fieldDraftFromCatalog(item, index = 0, styleDefaults = DEFAULT_STYLE_DEFAULTS) {
  return {
    fieldKey: item.key,
    label: item.label || item.key,
    fieldType: item.type || 'TEXT',
    useTemplateStyle: true,
    x: 60,
    y: 80 + index * 52,
    w: item.type === 'IMAGE' ? 180 : 260,
    h: item.type === 'IMAGE' ? 220 : 42,
    fontFamily: styleDefaults.fontFamily,
    fontSize: item.type === 'IMAGE' ? 16 : styleDefaults.fontSize,
    fontWeight: styleDefaults.fontWeight,
    textColor: styleDefaults.textColor,
    textAlign: styleDefaults.textAlign,
    lineHeight: styleDefaults.lineHeight,
    zIndex: index,
    sampleValue: item.sampleValue || '',
    formatPattern: item.formatPattern || '',
  }
}

function ImageMappingCanvas({ imageUrl, template, fields, selectedFieldId, styleDefaults, onSelectField, onChangeRect }) {
  const imgRef = React.useRef(null)
  const wrapRef = React.useRef(null)
  const [zoom, setZoom] = useState(0.55)
  const [dragging, setDragging] = useState(false)
  const [start, setStart] = useState(null)
  const [draftRect, setDraftRect] = useState(null)

  const canvasW = toNumber(template?.canvasWidth, 900)
  const canvasH = toNumber(template?.canvasHeight, 1260)
  const selectedField = fields.find((f) => String(f.id) === String(selectedFieldId)) || null

  useEffect(() => {
    setDraftRect(null)
  }, [selectedFieldId, imageUrl])

  const localPoint = (event) => {
    const wrap = wrapRef.current
    if (!wrap) return null
    const r = wrap.getBoundingClientRect()
    return {
      x: Math.max(0, Math.min(Math.round((event.clientX - r.left) / zoom), canvasW)),
      y: Math.max(0, Math.min(Math.round((event.clientY - r.top) / zoom), canvasH)),
    }
  }

  const handleDown = (event) => {
    if (!selectedField) return
    const point = localPoint(event)
    if (!point) return
    setDragging(true)
    setStart(point)
    setDraftRect({ x: point.x, y: point.y, w: 1, h: 1 })
  }

  const handleMove = (event) => {
    if (!dragging || !start) return
    const point = localPoint(event)
    if (!point) return
    setDraftRect({
      x: Math.min(start.x, point.x),
      y: Math.min(start.y, point.y),
      w: Math.max(1, Math.abs(point.x - start.x)),
      h: Math.max(1, Math.abs(point.y - start.y)),
    })
  }

  const handleUp = () => {
    if (!dragging) return
    setDragging(false)
    setStart(null)
    if (selectedField && draftRect && draftRect.w > 4 && draftRect.h > 4) {
      onChangeRect(selectedField.id, draftRect)
    }
  }

  return (
    <div className="min-w-0">
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <div className="text-sm font-semibold text-slate-800">좌표 미리보기</div>
        <div className="flex items-center gap-2 text-xs text-slate-600">
          <button type="button" className="rounded border px-2 py-1 hover:bg-slate-50" onClick={() => setZoom((z) => Math.max(0.25, Math.round((z - 0.1) * 100) / 100))}>-</button>
          <input type="range" min="0.25" max="1.4" step="0.05" value={zoom} onChange={(e) => setZoom(Number(e.target.value))} />
          <button type="button" className="rounded border px-2 py-1 hover:bg-slate-50" onClick={() => setZoom((z) => Math.min(1.4, Math.round((z + 0.1) * 100) / 100))}>+</button>
          <span className="w-12 text-right">{Math.round(zoom * 100)}%</span>
        </div>
      </div>
      <div className="overflow-auto rounded-lg border bg-slate-100 p-3">
        <div
          ref={wrapRef}
          className="relative select-none bg-white shadow-sm"
          style={{ width: canvasW * zoom, height: canvasH * zoom }}
          onMouseDown={handleDown}
          onMouseMove={handleMove}
          onMouseUp={handleUp}
          onMouseLeave={handleUp}
        >
          <div className="absolute left-0 top-0 origin-top-left" style={{ width: canvasW, height: canvasH, transform: `scale(${zoom})` }}>
            {imageUrl ? (
              <img ref={imgRef} src={imageUrl} alt="" className="h-full w-full object-contain" draggable={false} />
            ) : (
              <div className="flex h-full w-full items-center justify-center bg-slate-50 text-sm text-slate-400">
                기준 이미지 없음
              </div>
            )}
            {fields.map((field) => {
              const selected = String(field.id) === String(selectedFieldId)
              const isImage = field.fieldType === 'IMAGE'
              const style = effectiveFieldStyle(field, styleDefaults)
              return (
                <button
                  type="button"
                  key={field.id || field.fieldKey}
                  className={`absolute overflow-hidden border-2 text-left ${selected ? 'border-brand-sky bg-sky-300/20' : 'border-rose-400/70 bg-rose-200/15'}`}
                  style={{ left: field.x, top: field.y, width: field.w, height: field.h, zIndex: 10 + toNumber(field.zIndex, 0) }}
                  onClick={(event) => {
                    event.stopPropagation()
                    onSelectField(field.id)
                  }}
                >
                  <span className="absolute left-1 top-1 rounded bg-white/90 px-1 text-[10px] font-semibold text-slate-700 shadow-sm">
                    {field.label}
                  </span>
                  {!isImage ? (
                    <span
                      className="absolute inset-x-2 bottom-1 truncate"
                      style={{ color: style.textColor || '#111827', fontSize: Math.min(24, Math.max(10, toNumber(style.fontSize, 16))) }}
                    >
                      {field.sampleValue || field.label}
                    </span>
                  ) : null}
                </button>
              )
            })}
            {draftRect ? (
              <div
                className="absolute border-2 border-brand-navy bg-brand-navy/10"
                style={{ left: draftRect.x, top: draftRect.y, width: draftRect.w, height: draftRect.h, zIndex: 999 }}
              />
            ) : null}
          </div>
        </div>
      </div>
    </div>
  )
}

export default function ReportTemplatesPage() {
  const { show } = useToast()
  const [reports, setReports] = useState([])
  const [templates, setTemplates] = useState([])
  const [selectedReportCode, setSelectedReportCode] = useState('')
  const [selectedTemplateId, setSelectedTemplateId] = useState(null)
  const [detail, setDetail] = useState(null)
  const [fields, setFields] = useState([])
  const [selectedFieldId, setSelectedFieldId] = useState(null)
  const [fieldSearch, setFieldSearch] = useState('')
  const [catalogFields, setCatalogFields] = useState([])
  const [baseImageUrl, setBaseImageUrl] = useState('')
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [previewing, setPreviewing] = useState(false)
  const [duplicating, setDuplicating] = useState(false)
  const [approvalBusy, setApprovalBusy] = useState('')
  const [usageRows, setUsageRows] = useState([])
  const [usageLoading, setUsageLoading] = useState(false)
  const [fontOptions, setFontOptions] = useState(DEFAULT_FONT_OPTIONS)

  const selectedReport = reports.find((r) => r.code === selectedReportCode) || reports[0] || null
  const selectedField = fields.find((f) => String(f.id) === String(selectedFieldId)) || null

  const templateForm = detail || {
    reportCode: selectedReportCode,
    title: '',
    description: '',
    renderMode: selectedReport?.defaultRenderMode || 'HTML',
    baseImageFileId: '',
    htmlTemplateKey: '',
    canvasWidth: 900,
    canvasHeight: 1260,
    deviceScaleFactor: 2,
    configJson: buildTemplateConfigJson('', DEFAULT_STYLE_DEFAULTS),
    templateStatus: 'DRAFT',
    approvalStatus: 'NOT_REQUESTED',
    approvalStatusLabel: '미요청',
    versionNo: 1,
    active: false,
    defaultTemplate: false,
    userSelectable: false,
    selectionLabel: '',
    selectionDescription: '',
  }

  const templateStatus = templateForm.templateStatus || (templateForm.active ? 'PUBLISHED' : 'DRAFT')
  const isPublished = templateStatus === 'PUBLISHED'
  const userSelectableAllowed = Boolean(selectedReport?.userSelectableAllowed)
  const canSelectTemplate = isPublished && userSelectableAllowed
  const canPreviewImage = Boolean(selectedTemplateId) && ['PNG_OVERLAY', 'HYBRID'].includes(templateForm.renderMode || '')
  const templateConfig = useMemo(() => parseTemplateConfig(templateForm.configJson), [templateForm.configJson])
  const styleDefaults = useMemo(() => normalizeStyleDefaults(templateConfig.styleDefaults), [templateConfig.styleDefaults])
  const canPreviewHtml = Boolean(selectedTemplateId) && ['HTML', 'HYBRID'].includes(templateForm.renderMode || '')
  const htmlTemplateBody = typeof templateConfig.htmlTemplate === 'string' ? templateConfig.htmlTemplate : ''
  const selectedFieldStyle = selectedField ? effectiveFieldStyle(selectedField, styleDefaults) : styleDefaults
  const availableFontOptions = useMemo(
    () => mergeFontOptions(fontOptions, [styleDefaults.fontFamily, selectedFieldStyle.fontFamily]),
    [fontOptions, styleDefaults.fontFamily, selectedFieldStyle.fontFamily]
  )
  const defaultFontWeightOptions = useMemo(() => {
    const option = findFontOption(availableFontOptions, styleDefaults.fontFamily)
    return fontWeightsForOption(option, styleDefaults.fontWeight)
  }, [availableFontOptions, styleDefaults.fontFamily, styleDefaults.fontWeight])
  const selectedFieldFontWeightOptions = useMemo(() => {
    const option = findFontOption(availableFontOptions, selectedFieldStyle.fontFamily)
    return fontWeightsForOption(option, selectedFieldStyle.fontWeight)
  }, [availableFontOptions, selectedFieldStyle.fontFamily, selectedFieldStyle.fontWeight])
  const renderModes = Array.isArray(selectedReport?.supportedModes) && selectedReport.supportedModes.length
    ? RENDER_MODES.filter((mode) => selectedReport.supportedModes.includes(mode.value))
    : RENDER_MODES
  const fieldMap = useMemo(() => new Map(fields.map((f) => [f.fieldKey, f])), [fields])

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      setLoading(true)
      try {
        const res = await api.get('/ops/report-templates/reports')
        const data = unwrap(res)
        const list = Array.isArray(data) ? data : []
        if (cancelled) return
        setReports(list)
        setSelectedReportCode((prev) => prev || list[0]?.code || '')
      } catch {
        if (!cancelled) show('출력물 목록을 불러오지 못했습니다.', 'error')
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => { cancelled = true }
  }, [show])

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        const res = await api.get('/ops/report-output-fonts/options', { __silent: true })
        const list = Array.isArray(unwrap(res)) ? unwrap(res) : []
        if (!cancelled && list.length > 0) {
          setFontOptions(mergeFontOptions(list))
        }
      } catch {
        if (!cancelled) {
          setFontOptions(DEFAULT_FONT_OPTIONS)
        }
      }
    })()
    return () => { cancelled = true }
  }, [])

  const loadTemplates = React.useCallback(async (reportCode) => {
    if (!reportCode) return
    const res = await api.get('/ops/report-templates', { params: { reportCode } })
    const list = Array.isArray(unwrap(res)) ? unwrap(res) : []
    setTemplates(list)
    setSelectedTemplateId((prev) => {
      if (prev && list.some((item) => String(item.id) === String(prev))) return prev
      return list[0]?.id || null
    })
    if (!list.length) {
      setDetail(null)
      setFields([])
      setSelectedFieldId(null)
    }
  }, [])

  useEffect(() => {
    if (!selectedReportCode) return
    void loadTemplates(selectedReportCode)
  }, [selectedReportCode, loadTemplates])

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      if (!selectedTemplateId) return
      try {
        const res = await api.get(`/ops/report-templates/${selectedTemplateId}`)
        const payload = unwrap(res) || null
        if (cancelled) return
        setDetail(payload)
        setFields(Array.isArray(payload?.fields) ? payload.fields : [])
        setSelectedFieldId((prev) => {
          const rows = Array.isArray(payload?.fields) ? payload.fields : []
          if (prev && rows.some((item) => String(item.id) === String(prev))) return prev
          return rows[0]?.id || null
        })
      } catch {
        if (!cancelled) show('템플릿 정보를 불러오지 못했습니다.', 'error')
      }
    })()
    return () => { cancelled = true }
  }, [selectedTemplateId, show])

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      if (!selectedReportCode) return
      try {
        const res = await api.get('/ops/report-templates/fields', {
          params: { reportCode: selectedReportCode, q: fieldSearch.trim() || undefined },
        })
        if (!cancelled) setCatalogFields(Array.isArray(unwrap(res)) ? unwrap(res) : [])
      } catch {
        if (!cancelled) setCatalogFields([])
      }
    })()
    return () => { cancelled = true }
  }, [selectedReportCode, fieldSearch])

  useEffect(() => {
    const fileId = detail?.baseImageFileId
    if (!fileId) {
      setBaseImageUrl((prev) => {
        try { if (prev) URL.revokeObjectURL(prev) } catch {}
        return ''
      })
      return undefined
    }
    let cancelled = false
    ;(async () => {
      try {
        const res = await api.get(`/files/${fileId}/preview`, { responseType: 'blob' })
        const blob = new Blob([res.data], { type: res.headers?.['content-type'] || 'image/png' })
        const url = URL.createObjectURL(blob)
        if (cancelled) {
          URL.revokeObjectURL(url)
          return
        }
        setBaseImageUrl((prev) => {
          try { if (prev) URL.revokeObjectURL(prev) } catch {}
          return url
        })
      } catch {
        if (!cancelled) setBaseImageUrl('')
      }
    })()
    return () => { cancelled = true }
  }, [detail?.baseImageFileId])

  const updateDetail = (patch) => {
    setDetail((prev) => ({ ...(prev || templateForm), ...patch }))
  }

  const updateStyleDefaults = (patch) => {
    const next = normalizeStyleDefaults({ ...styleDefaults, ...patch })
    updateDetail({ configJson: buildTemplateConfigJson(templateForm.configJson, next) })
  }

  const updateDefaultFontFamily = (fontFamily) => {
    const option = findFontOption(availableFontOptions, fontFamily)
    updateStyleDefaults({
      fontFamily,
      fontWeight: closestFontWeight(option, styleDefaults.fontWeight),
    })
  }

  const updateTemplateConfig = (patch) => {
    updateDetail({ configJson: buildTemplateConfigJson(templateForm.configJson, styleDefaults, patch) })
  }

  const saveTemplate = async () => {
    if (!templateForm.reportCode) return show('출력물을 선택해 주세요.', 'error')
    if (!String(templateForm.title || '').trim()) return show('템플릿명을 입력해 주세요.', 'error')
    setSaving(true)
    try {
      const nextStatus = templateForm.templateStatus || (templateForm.active ? 'PUBLISHED' : 'DRAFT')
      const nextPublished = nextStatus === 'PUBLISHED'
      const body = {
        reportCode: templateForm.reportCode,
        title: templateForm.title,
        description: templateForm.description || '',
        renderMode: templateForm.renderMode,
        baseImageFileId: templateForm.baseImageFileId || '',
        htmlTemplateKey: templateForm.htmlTemplateKey || '',
        canvasWidth: toNumber(templateForm.canvasWidth, 900),
        canvasHeight: toNumber(templateForm.canvasHeight, 1260),
        deviceScaleFactor: Number(templateForm.deviceScaleFactor || 2),
        configJson: buildTemplateConfigJson(templateForm.configJson, styleDefaults),
        templateStatus: nextStatus,
        active: nextPublished,
        defaultTemplate: nextPublished && Boolean(templateForm.defaultTemplate),
        userSelectable: nextPublished && userSelectableAllowed && Boolean(templateForm.userSelectable),
        selectionLabel: templateForm.selectionLabel || '',
        selectionDescription: templateForm.selectionDescription || '',
      }
      const res = selectedTemplateId
        ? await api.patch(`/ops/report-templates/${selectedTemplateId}`, body)
        : await api.post('/ops/report-templates', body)
      const saved = unwrap(res)
      show('템플릿을 저장했습니다.', 'success')
      await loadTemplates(saved?.reportCode || selectedReportCode)
      if (saved?.id) setSelectedTemplateId(saved.id)
    } catch (err) {
      show(err?.response?.data?.message || '템플릿 저장에 실패했습니다.', 'error')
    } finally {
      setSaving(false)
    }
  }

  const downloadTemplatePreview = async () => {
    if (!selectedTemplateId) return show('먼저 템플릿을 저장해 주세요.', 'error')
    if (!canPreviewImage) return show('PNG 오버레이 또는 혼합 템플릿만 미리보기를 생성할 수 있습니다.', 'error')
    setPreviewing(true)
    try {
      const res = await api.get(`/ops/report-templates/${selectedTemplateId}/preview-image`, { responseType: 'blob' })
      const blob = new Blob([res.data], { type: res.headers?.['content-type'] || 'image/png' })
      const filename = filenameFromContentDisposition(
        res.headers?.['content-disposition'] || res.headers?.['Content-Disposition'],
        `${templateForm.title || '출력물템플릿'}_미리보기.png`
      )
      downloadBlob(blob, filename)
      show('미리보기 이미지를 생성했습니다.', 'success')
    } catch (err) {
      show(err?.response?.data?.message || '미리보기 생성에 실패했습니다.', 'error')
    } finally {
      setPreviewing(false)
    }
  }

  const downloadTemplateHtmlPreview = async () => {
    if (!selectedTemplateId) return show('먼저 템플릿을 저장해 주세요.', 'error')
    if (!canPreviewHtml) return show('HTML 또는 혼합 템플릿만 미리보기를 생성할 수 있습니다.', 'error')
    setPreviewing(true)
    try {
      const res = await api.get(`/ops/report-templates/${selectedTemplateId}/preview-html`, { responseType: 'blob' })
      const blob = new Blob([res.data], { type: res.headers?.['content-type'] || 'text/html' })
      const filename = filenameFromContentDisposition(
        res.headers?.['content-disposition'] || res.headers?.['Content-Disposition'],
        `${templateForm.title || '출력물템플릿'}_미리보기.html`
      )
      downloadBlob(blob, filename)
      show('HTML 미리보기를 생성했습니다.', 'success')
    } catch (err) {
      show(err?.response?.data?.message || 'HTML 미리보기 생성에 실패했습니다.', 'error')
    } finally {
      setPreviewing(false)
    }
  }

  const duplicateTemplate = async () => {
    if (!selectedTemplateId) return show('복제할 템플릿을 선택해 주세요.', 'error')
    setDuplicating(true)
    try {
      const res = await api.post(`/ops/report-templates/${selectedTemplateId}/duplicate`)
      const saved = unwrap(res)
      show('템플릿을 복제했습니다.', 'success')
      await loadTemplates(saved?.reportCode || selectedReportCode)
      if (saved?.id) setSelectedTemplateId(saved.id)
    } catch (err) {
      show(err?.response?.data?.message || '템플릿 복제에 실패했습니다.', 'error')
    } finally {
      setDuplicating(false)
    }
  }

  const loadTemplateUsages = React.useCallback(async (templateId = selectedTemplateId) => {
    if (!templateId) {
      setUsageRows([])
      setUsageLoading(false)
      return
    }
    setUsageLoading(true)
    try {
      const res = await api.get(`/ops/report-templates/${templateId}/usages`, { __silent: true })
      const rows = unwrap(res)
      setUsageRows(Array.isArray(rows) ? rows : [])
    } catch {
      setUsageRows([])
    } finally {
      setUsageLoading(false)
    }
  }, [selectedTemplateId])

  const applyTemplateMutationResult = async (saved) => {
    if (saved) {
      setDetail(saved)
      setFields(Array.isArray(saved.fields) ? saved.fields : fields)
    }
    await loadTemplates(saved?.reportCode || selectedReportCode)
    if (saved?.id) {
      setSelectedTemplateId(saved.id)
      await loadTemplateUsages(saved.id)
    }
  }

  const requestTemplateApproval = async () => {
    if (!selectedTemplateId) return show('승인 요청할 템플릿을 선택해 주세요.', 'error')
    setApprovalBusy('request')
    try {
      const res = await api.post(`/ops/report-templates/${selectedTemplateId}/request-approval`)
      const saved = unwrap(res)
      await applyTemplateMutationResult(saved)
      show('승인 요청 상태로 변경했습니다.', 'success')
    } catch (err) {
      show(err?.response?.data?.message || '승인 요청에 실패했습니다.', 'error')
    } finally {
      setApprovalBusy('')
    }
  }

  const approveTemplate = async () => {
    if (!selectedTemplateId) return show('승인할 템플릿을 선택해 주세요.', 'error')
    if (!window.confirm('선택한 템플릿을 승인하고 게시 상태로 전환할까요?')) return
    setApprovalBusy('approve')
    try {
      const res = await api.post(`/ops/report-templates/${selectedTemplateId}/approve`)
      const saved = unwrap(res)
      await applyTemplateMutationResult(saved)
      show('템플릿을 승인했습니다.', 'success')
    } catch (err) {
      show(err?.response?.data?.message || '템플릿 승인에 실패했습니다.', 'error')
    } finally {
      setApprovalBusy('')
    }
  }

  const rejectTemplate = async () => {
    if (!selectedTemplateId) return show('반려할 템플릿을 선택해 주세요.', 'error')
    const reason = window.prompt('반려 사유를 입력해 주세요.', templateForm.rejectionReason || '')
    if (reason == null) return
    setApprovalBusy('reject')
    try {
      const res = await api.post(`/ops/report-templates/${selectedTemplateId}/reject`, { reason })
      const saved = unwrap(res)
      await applyTemplateMutationResult(saved)
      show('템플릿을 반려했습니다.', 'success')
    } catch (err) {
      show(err?.response?.data?.message || '템플릿 반려에 실패했습니다.', 'error')
    } finally {
      setApprovalBusy('')
    }
  }

  const createTemplate = () => {
    setSelectedTemplateId(null)
    setUsageRows([])
    setDetail({
      reportCode: selectedReportCode,
      title: `${selectedReport?.label || '출력물'} 템플릿`,
      description: '',
      renderMode: selectedReport?.defaultRenderMode || 'HTML',
      baseImageFileId: '',
      htmlTemplateKey: 'default',
      canvasWidth: 900,
      canvasHeight: 1260,
      deviceScaleFactor: 2,
      configJson: buildTemplateConfigJson('', DEFAULT_STYLE_DEFAULTS),
      templateStatus: 'DRAFT',
      approvalStatus: 'NOT_REQUESTED',
      approvalStatusLabel: '미요청',
      versionNo: 1,
      active: false,
      defaultTemplate: false,
      userSelectable: false,
      selectionLabel: '',
      selectionDescription: '',
      fields: [],
    })
    setFields([])
    setSelectedFieldId(null)
  }

  const uploadBaseImage = async (event) => {
    const file = event.target.files?.[0]
    event.target.value = ''
    if (!file) return
    setUploading(true)
    try {
      const fd = new FormData()
      fd.append('file', file)
      const res = await api.post('/files', fd, { headers: { 'Content-Type': 'multipart/form-data' } })
      const payload = unwrap(res) || {}
      const fileId = payload.fileId || payload.id
      if (!fileId) throw new Error('fileId missing')
      updateDetail({ baseImageFileId: fileId })
      show('기준 이미지를 업로드했습니다.', 'success')
    } catch {
      show('기준 이미지 업로드에 실패했습니다.', 'error')
    } finally {
      setUploading(false)
    }
  }

  const addField = async (catalog) => {
    if (!selectedTemplateId) return show('먼저 템플릿을 저장해 주세요.', 'error')
    const draft = fieldDraftFromCatalog(catalog, fields.length, styleDefaults)
    try {
      const res = await api.post(`/ops/report-templates/${selectedTemplateId}/fields`, draft)
      const saved = unwrap(res)
      const next = [...fields.filter((f) => f.fieldKey !== saved.fieldKey), saved]
      setFields(next)
      setSelectedFieldId(saved.id)
      show('필드를 추가했습니다.', 'success')
    } catch (err) {
      show(err?.response?.data?.message || '필드 추가에 실패했습니다.', 'error')
    }
  }

  const updateFieldLocal = (fieldId, patch) => {
    setFields((prev) => prev.map((f) => String(f.id) === String(fieldId) ? { ...f, ...patch } : f))
  }

  const updateFieldFontFamily = (fieldId, fontFamily) => {
    const option = findFontOption(availableFontOptions, fontFamily)
    updateFieldLocal(fieldId, {
      fontFamily,
      fontWeight: closestFontWeight(option, selectedFieldStyle.fontWeight),
    })
  }

  const saveField = async (field = selectedField) => {
    if (!selectedTemplateId || !field?.id) return
    try {
      const res = await api.patch(`/ops/report-templates/${selectedTemplateId}/fields/${field.id}`, field)
      const saved = unwrap(res)
      setFields((prev) => prev.map((f) => String(f.id) === String(saved.id) ? saved : f))
      show('필드 좌표를 저장했습니다.', 'success')
    } catch (err) {
      show(err?.response?.data?.message || '필드 저장에 실패했습니다.', 'error')
    }
  }

  const deleteField = async () => {
    if (!selectedTemplateId || !selectedField?.id) return
    if (!window.confirm('선택한 필드를 삭제할까요?')) return
    try {
      await api.delete(`/ops/report-templates/${selectedTemplateId}/fields/${selectedField.id}`)
      const next = fields.filter((f) => String(f.id) !== String(selectedField.id))
      setFields(next)
      setSelectedFieldId(next[0]?.id || null)
      show('필드를 삭제했습니다.', 'success')
    } catch {
      show('필드 삭제에 실패했습니다.', 'error')
    }
  }

  useEffect(() => {
    void loadTemplateUsages(selectedTemplateId)
  }, [selectedTemplateId, loadTemplateUsages])

  if (loading) return <Loading />

  return (
    <div className="space-y-5">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">템플릿관리 &gt; 출력물 템플릿</h1>
          <p className="mt-1 text-sm text-slate-500">HTML, PNG 오버레이, 혼합 출력 템플릿을 관리합니다.</p>
        </div>
        <MenuGuideButton slug="template-management" title="템플릿관리 가이드" />
      </header>

      <div className="grid gap-4 xl:grid-cols-[320px_minmax(0,1fr)]">
        <aside className="space-y-3 rounded-lg border bg-white p-3">
          <label className="block text-sm">
            <span className="mb-1 block font-semibold text-slate-700">출력물</span>
            <select
              className="w-full rounded border px-3 py-2"
              value={selectedReportCode}
              onChange={(e) => {
                setSelectedReportCode(e.target.value)
                setSelectedTemplateId(null)
              }}
            >
              {reports.map((report) => (
                <option key={report.code} value={report.code}>{report.label}</option>
              ))}
            </select>
          </label>
          {selectedReport ? (
            <div className="rounded border bg-slate-50 p-3 text-sm">
              <div className="flex flex-wrap items-center gap-1.5">
                <span className="rounded bg-white px-2 py-0.5 text-xs font-semibold text-slate-700 ring-1 ring-slate-200">{selectedReport.outputTypeLabel || '출력물'}</span>
                <span className="rounded bg-white px-2 py-0.5 text-xs font-semibold text-slate-700 ring-1 ring-slate-200">{selectedReport.integrationStatusLabel || '연결 준비'}</span>
              </div>
              <div className="mt-2 font-semibold text-slate-900">{selectedReport.managementLabel || '템플릿 관리'}</div>
              <div className="mt-1 text-xs text-slate-500">{selectedReport.selectionGuideLabel || '운영 기본값 사용'}</div>
            </div>
          ) : null}
          <button type="button" className="w-full rounded bg-brand-navy px-3 py-2 text-sm font-semibold text-white disabled:opacity-50" onClick={createTemplate} disabled={!selectedReportCode}>
            새 템플릿
          </button>
          <div className="max-h-[calc(100vh-280px)] space-y-2 overflow-auto">
            {templates.map((item) => (
              <button
                type="button"
                key={item.id}
                className={`w-full rounded border px-3 py-2 text-left text-sm ${String(selectedTemplateId) === String(item.id) ? 'border-brand-navy bg-sky-50 ring-1 ring-brand-navy/10' : 'hover:bg-slate-50'}`}
                onClick={() => setSelectedTemplateId(item.id)}
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="truncate font-semibold text-slate-900">{item.title}</span>
                  <span className={`shrink-0 rounded px-1.5 py-0.5 text-[11px] font-semibold ring-1 ${statusBadgeClass(item.templateStatus)}`}>
                    {item.templateStatusLabel || '작성중'}
                  </span>
                </div>
                <div className="mt-1 flex flex-wrap items-center gap-1 text-xs text-slate-500">
                  <span>{item.renderModeLabel}</span>
                  <span>{item.fieldCount || 0}개 필드</span>
                  <span>v{item.versionNo || 1}</span>
                  <span className={`rounded px-1.5 py-0.5 font-semibold ring-1 ${approvalBadgeClass(item.approvalStatus)}`}>
                    {item.approvalStatusLabel || '미요청'}
                  </span>
                  {item.defaultTemplate ? <span className="rounded bg-sky-50 px-1.5 py-0.5 font-semibold text-sky-700">기본</span> : null}
                  {item.userSelectable ? <span className="rounded bg-emerald-50 px-1.5 py-0.5 font-semibold text-emerald-700">선택 가능</span> : null}
                </div>
              </button>
            ))}
            {!templates.length ? (
              <div className="rounded border border-dashed px-3 py-8 text-center text-sm text-slate-500">템플릿이 없습니다</div>
            ) : null}
          </div>
        </aside>

        <main className="space-y-4">
          <section className="rounded-lg border bg-white p-4">
            <div className="grid gap-3 lg:grid-cols-4">
              <label className="block text-sm lg:col-span-2">
                <span className="mb-1 block font-semibold text-slate-700">템플릿명</span>
                <input className="w-full rounded border px-3 py-2" value={templateForm.title || ''} onChange={(e) => updateDetail({ title: e.target.value })} />
              </label>
              <label className="block text-sm">
                <span className="mb-1 block font-semibold text-slate-700">출력 방식</span>
                <select className="w-full rounded border px-3 py-2" value={templateForm.renderMode || 'HTML'} onChange={(e) => updateDetail({ renderMode: e.target.value })}>
                  {renderModes.map((mode) => <option key={mode.value} value={mode.value}>{mode.label}</option>)}
                </select>
              </label>
              <label className="block text-sm">
                <span className="mb-1 block font-semibold text-slate-700">상태</span>
                <select
                  className="w-full rounded border px-3 py-2"
                  value={templateStatus}
                  onChange={(e) => {
                    const next = e.target.value
                    updateDetail({
                      templateStatus: next,
                      active: next === 'PUBLISHED',
                      defaultTemplate: next === 'PUBLISHED' ? Boolean(templateForm.defaultTemplate) : false,
                      userSelectable: next === 'PUBLISHED' ? Boolean(templateForm.userSelectable) : false,
                    })
                  }}
                >
                  {TEMPLATE_STATUS_OPTIONS.map((status) => <option key={status.value} value={status.value}>{status.label}</option>)}
                </select>
              </label>
              <label className="block text-sm">
                <span className="mb-1 block font-semibold text-slate-700">HTML 키</span>
                <input className="w-full rounded border px-3 py-2" value={templateForm.htmlTemplateKey || ''} onChange={(e) => updateDetail({ htmlTemplateKey: e.target.value })} />
              </label>
              <label className="block text-sm">
                <span className="mb-1 block font-semibold text-slate-700">폭</span>
                <input type="number" className="w-full rounded border px-3 py-2" value={templateForm.canvasWidth || 900} onChange={(e) => updateDetail({ canvasWidth: e.target.value })} />
              </label>
              <label className="block text-sm">
                <span className="mb-1 block font-semibold text-slate-700">높이</span>
                <input type="number" className="w-full rounded border px-3 py-2" value={templateForm.canvasHeight || 1260} onChange={(e) => updateDetail({ canvasHeight: e.target.value })} />
              </label>
              <label className="block text-sm">
                <span className="mb-1 block font-semibold text-slate-700">배율</span>
                <input type="number" step="0.25" className="w-full rounded border px-3 py-2" value={templateForm.deviceScaleFactor || 2} onChange={(e) => updateDetail({ deviceScaleFactor: e.target.value })} />
              </label>
              <div className="flex items-end gap-3">
                <label className="inline-flex min-h-[42px] items-center gap-2 text-sm font-semibold text-slate-700">
                  <input type="checkbox" className="size-4" checked={isPublished && Boolean(templateForm.defaultTemplate)} onChange={(e) => updateDetail({ defaultTemplate: e.target.checked })} disabled={!isPublished} />
                  기본
                </label>
                <label className="inline-flex min-h-[42px] items-center gap-2 text-sm font-semibold text-slate-700">
                  <input type="checkbox" className="size-4" checked={canSelectTemplate && Boolean(templateForm.userSelectable)} onChange={(e) => updateDetail({ userSelectable: e.target.checked })} disabled={!canSelectTemplate} />
                  선택 가능
                </label>
              </div>
              <label className="block text-sm lg:col-span-2">
                <span className="mb-1 block font-semibold text-slate-700">설명</span>
                <input className="w-full rounded border px-3 py-2" value={templateForm.description || ''} onChange={(e) => updateDetail({ description: e.target.value })} />
              </label>
              <label className="block text-sm">
                <span className="mb-1 block font-semibold text-slate-700">선택 표시명</span>
                <input className="w-full rounded border px-3 py-2 disabled:bg-slate-50" value={templateForm.selectionLabel || ''} onChange={(e) => updateDetail({ selectionLabel: e.target.value })} disabled={!userSelectableAllowed} />
              </label>
              <label className="block text-sm">
                <span className="mb-1 block font-semibold text-slate-700">선택 설명</span>
                <input className="w-full rounded border px-3 py-2 disabled:bg-slate-50" value={templateForm.selectionDescription || ''} onChange={(e) => updateDetail({ selectionDescription: e.target.value })} disabled={!userSelectableAllowed} />
              </label>
              <div className="border-t pt-3 lg:col-span-4">
                <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                  <div className="flex flex-wrap items-center gap-2 text-sm">
                    <span className="font-semibold text-slate-800">운영 상태</span>
                    <span className="rounded bg-white px-2 py-0.5 text-xs font-semibold text-slate-700 ring-1 ring-slate-200">
                      v{templateForm.versionNo || 1}
                    </span>
                    <span className={`rounded px-2 py-0.5 text-xs font-semibold ring-1 ${approvalBadgeClass(templateForm.approvalStatus)}`}>
                      {templateForm.approvalStatusLabel || '미요청'}
                    </span>
                    {templateForm.sourceTemplateId ? (
                      <span className="rounded bg-white px-2 py-0.5 text-xs font-semibold text-slate-500 ring-1 ring-slate-200">
                        원본 #{templateForm.sourceTemplateId}
                      </span>
                    ) : null}
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <button
                      type="button"
                      className="rounded border px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
                      onClick={requestTemplateApproval}
                      disabled={!selectedTemplateId || Boolean(approvalBusy)}
                    >
                      {approvalBusy === 'request' ? '요청 중...' : '승인 요청'}
                    </button>
                    <button
                      type="button"
                      className="rounded border border-emerald-200 bg-emerald-50 px-3 py-1.5 text-xs font-semibold text-emerald-800 hover:bg-emerald-100 disabled:cursor-not-allowed disabled:opacity-50"
                      onClick={approveTemplate}
                      disabled={!selectedTemplateId || Boolean(approvalBusy)}
                    >
                      {approvalBusy === 'approve' ? '승인 중...' : '승인/게시'}
                    </button>
                    <button
                      type="button"
                      className="rounded border border-rose-200 bg-rose-50 px-3 py-1.5 text-xs font-semibold text-rose-700 hover:bg-rose-100 disabled:cursor-not-allowed disabled:opacity-50"
                      onClick={rejectTemplate}
                      disabled={!selectedTemplateId || Boolean(approvalBusy)}
                    >
                      {approvalBusy === 'reject' ? '반려 중...' : '반려'}
                    </button>
                  </div>
                </div>
                <div className="grid gap-3 text-xs text-slate-600 md:grid-cols-3">
                  <div>
                    <div className="font-semibold text-slate-700">요청</div>
                    <div>{formatDateTime(templateForm.approvalRequestedAt)}</div>
                    {templateForm.approvalRequestedByName ? <div>{templateForm.approvalRequestedByName}</div> : null}
                  </div>
                  <div>
                    <div className="font-semibold text-slate-700">승인</div>
                    <div>{formatDateTime(templateForm.approvedAt)}</div>
                    {templateForm.approvedByName ? <div>{templateForm.approvedByName}</div> : null}
                  </div>
                  <div>
                    <div className="font-semibold text-slate-700">반려</div>
                    <div>{formatDateTime(templateForm.rejectedAt)}</div>
                    {templateForm.rejectedByName ? <div>{templateForm.rejectedByName}</div> : null}
                    {templateForm.rejectionReason ? <div className="mt-1 text-rose-700">{templateForm.rejectionReason}</div> : null}
                  </div>
                </div>
                <div className="mt-4">
                  <div className="mb-2 flex items-center justify-between gap-2">
                    <div className="text-sm font-semibold text-slate-800">최근 사용 이력</div>
                    <button
                      type="button"
                      className="rounded border px-2 py-1 text-xs font-semibold text-slate-600 hover:bg-slate-50 disabled:opacity-50"
                      onClick={() => loadTemplateUsages(selectedTemplateId)}
                      disabled={!selectedTemplateId || usageLoading}
                    >
                      {usageLoading ? '갱신 중...' : '갱신'}
                    </button>
                  </div>
                  <div className="max-h-48 overflow-auto rounded border">
                    <table className="min-w-full divide-y divide-slate-200 text-xs">
                      <thead className="bg-slate-50 text-slate-600">
                        <tr>
                          <th className="px-2 py-1.5 text-left font-semibold">시간</th>
                          <th className="px-2 py-1.5 text-left font-semibold">형식</th>
                          <th className="px-2 py-1.5 text-left font-semibold">참조</th>
                          <th className="px-2 py-1.5 text-left font-semibold">사용자</th>
                          <th className="px-2 py-1.5 text-left font-semibold">결과</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100 bg-white">
                        {usageRows.map((row) => (
                          <tr key={row.id}>
                            <td className="px-2 py-1.5 text-slate-600">{formatDateTime(row.createdAt)}</td>
                            <td className="px-2 py-1.5 font-semibold text-slate-700">{row.outputFormat || row.renderMode || '-'}</td>
                            <td className="px-2 py-1.5 text-slate-600">{[row.refType, row.refId].filter(Boolean).join(' / ') || '-'}</td>
                            <td className="px-2 py-1.5 text-slate-600">{row.actorName || '-'}</td>
                            <td className={`px-2 py-1.5 font-semibold ${row.success ? 'text-emerald-700' : 'text-rose-700'}`}>
                              {row.success ? '성공' : (row.errorMessage || '실패')}
                            </td>
                          </tr>
                        ))}
                        {!usageRows.length ? (
                          <tr>
                            <td className="px-2 py-6 text-center text-slate-500" colSpan={5}>
                              {usageLoading ? '이력을 불러오는 중입니다' : '사용 이력이 없습니다'}
                            </td>
                          </tr>
                        ) : null}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
              <div className="rounded border bg-slate-50 p-3 lg:col-span-4">
                <div className="mb-3 text-sm font-semibold text-slate-800">기본 스타일</div>
                <div className="grid gap-3 md:grid-cols-3 xl:grid-cols-6">
                  <label className="block text-xs font-semibold text-slate-600">
                    폰트
                    <select className="mt-1 w-full rounded border bg-white px-2 py-1.5 text-sm" value={styleDefaults.fontFamily} onChange={(e) => updateDefaultFontFamily(e.target.value)}>
                      {availableFontOptions.map((option) => (
                        <option key={fontOptionKey(option)} value={fontOptionKey(option)}>{fontOptionLabel(option)}</option>
                      ))}
                    </select>
                  </label>
                  <label className="block text-xs font-semibold text-slate-600">
                    크기
                    <input type="number" className="mt-1 w-full rounded border bg-white px-2 py-1.5 text-sm" value={styleDefaults.fontSize} onChange={(e) => updateStyleDefaults({ fontSize: Number(e.target.value) })} />
                  </label>
                  <label className="block text-xs font-semibold text-slate-600">
                    굵기
                    <select className="mt-1 w-full rounded border bg-white px-2 py-1.5 text-sm" value={styleDefaults.fontWeight} onChange={(e) => updateStyleDefaults({ fontWeight: e.target.value })}>
                      {defaultFontWeightOptions.map((weight) => (
                        <option key={weight} value={weight}>{fontWeightLabel(weight)}</option>
                      ))}
                    </select>
                  </label>
                  <label className="block text-xs font-semibold text-slate-600">
                    색상
                    <div className="mt-1 flex rounded border bg-white">
                      <input type="color" className="h-[34px] w-10 border-r bg-white p-1" value={styleDefaults.textColor} onChange={(e) => updateStyleDefaults({ textColor: e.target.value })} />
                      <input className="min-w-0 flex-1 px-2 py-1.5 text-sm" value={styleDefaults.textColor} onChange={(e) => updateStyleDefaults({ textColor: e.target.value })} />
                    </div>
                  </label>
                  <label className="block text-xs font-semibold text-slate-600">
                    정렬
                    <select className="mt-1 w-full rounded border bg-white px-2 py-1.5 text-sm" value={styleDefaults.textAlign} onChange={(e) => updateStyleDefaults({ textAlign: e.target.value })}>
                      <option value="LEFT">왼쪽</option>
                      <option value="CENTER">가운데</option>
                      <option value="RIGHT">오른쪽</option>
                    </select>
                  </label>
                  <label className="block text-xs font-semibold text-slate-600">
                    줄간격
                    <input type="number" step="0.05" className="mt-1 w-full rounded border bg-white px-2 py-1.5 text-sm" value={styleDefaults.lineHeight} onChange={(e) => updateStyleDefaults({ lineHeight: Number(e.target.value) })} />
                  </label>
                </div>
              </div>
              {['HTML', 'HYBRID'].includes(templateForm.renderMode || '') ? (
                <label className="block text-sm lg:col-span-4">
                  <span className="mb-1 block font-semibold text-slate-700">HTML 본문</span>
                  <textarea
                    className="min-h-44 w-full rounded border px-3 py-2 font-mono text-xs leading-5"
                    value={htmlTemplateBody}
                    onChange={(e) => updateTemplateConfig({ htmlTemplate: e.target.value })}
                    placeholder="<section class=&quot;report-section&quot;>{{{coverImageBlock}}}<h1>{{templateTitle}}</h1></section>"
                  />
                </label>
              ) : null}
              <div className="flex flex-wrap items-end gap-2">
                <label className="block text-sm">
                  <span className="mb-1 block font-semibold text-slate-700">기준 PNG</span>
                  <input type="file" accept="image/png,image/jpeg,image/webp" className="block w-full text-xs" onChange={uploadBaseImage} disabled={uploading} />
                </label>
              </div>
            </div>
            <div className="mt-4 flex justify-end gap-2">
              <button type="button" className="rounded border px-3 py-2 text-sm hover:bg-slate-50" onClick={() => selectedTemplateId && loadTemplates(selectedReportCode)}>목록 새로고침</button>
              <button
                type="button"
                className="rounded border px-3 py-2 text-sm hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
                onClick={duplicateTemplate}
                disabled={!selectedTemplateId || duplicating}
              >
                {duplicating ? '복제 중...' : '복제'}
              </button>
              <button
                type="button"
                className="rounded border px-3 py-2 text-sm hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
                onClick={downloadTemplateHtmlPreview}
                disabled={!canPreviewHtml || previewing}
              >
                HTML 미리보기
              </button>
              <button
                type="button"
                className="rounded border px-3 py-2 text-sm hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
                onClick={downloadTemplatePreview}
                disabled={!canPreviewImage || previewing}
              >
                {previewing ? '미리보기 생성 중...' : 'PNG 미리보기'}
              </button>
              <button type="button" className="rounded bg-brand-navy px-4 py-2 text-sm font-semibold text-white disabled:opacity-50" onClick={saveTemplate} disabled={saving}>
                {saving ? '저장 중...' : '저장'}
              </button>
            </div>
          </section>

          <section className="grid gap-4 2xl:grid-cols-[minmax(0,1fr)_360px]">
            <div className="rounded-lg border bg-white p-4">
              <ImageMappingCanvas
                imageUrl={baseImageUrl}
                template={templateForm}
                fields={fields}
                selectedFieldId={selectedFieldId}
                styleDefaults={styleDefaults}
                onSelectField={setSelectedFieldId}
                onChangeRect={(fieldId, rect) => updateFieldLocal(fieldId, rect)}
              />
            </div>

            <div className="space-y-4">
              <div className="rounded-lg border bg-white p-4">
                <div className="mb-2 text-sm font-semibold text-slate-800">필드 검색</div>
                <input className="mb-2 w-full rounded border px-3 py-2 text-sm" value={fieldSearch} onChange={(e) => setFieldSearch(e.target.value)} placeholder="필드명 검색" />
                <div className="max-h-64 space-y-2 overflow-auto">
                  {catalogFields.map((item) => {
                    const exists = fieldMap.has(item.key)
                    return (
                      <button
                        type="button"
                        key={item.key}
                        className={`w-full rounded border px-3 py-2 text-left text-sm ${exists ? 'bg-slate-50 text-slate-400' : 'hover:bg-slate-50'}`}
                        onClick={() => !exists && addField(item)}
                        disabled={exists}
                      >
                        <div className="flex items-center justify-between gap-2">
                          <span className="font-semibold">{item.label}</span>
                          <span className="text-[11px]">{FIELD_TYPE_LABELS[item.type] || item.type}</span>
                        </div>
                        {item.description ? <div className="mt-0.5 text-xs text-slate-500">{item.description}</div> : null}
                      </button>
                    )
                  })}
                </div>
              </div>

              <div className="rounded-lg border bg-white p-4">
                <div className="mb-3 flex items-center justify-between gap-2">
                  <div className="text-sm font-semibold text-slate-800">선택 필드</div>
                  <button type="button" className="rounded border border-rose-200 px-2 py-1 text-xs text-rose-700 hover:bg-rose-50 disabled:opacity-50" onClick={deleteField} disabled={!selectedField}>
                    삭제
                  </button>
                </div>
                {selectedField ? (
                  <div className="space-y-3">
                    <label className="block text-sm">
                      <span className="mb-1 block font-semibold text-slate-700">표시명</span>
                      <input className="w-full rounded border px-3 py-2" value={selectedField.label || ''} onChange={(e) => updateFieldLocal(selectedField.id, { label: e.target.value })} />
                    </label>
                    <div className="grid grid-cols-4 gap-2">
                      {['x', 'y', 'w', 'h'].map((key) => (
                        <label key={key} className="block text-xs font-semibold text-slate-600">
                          {key.toUpperCase()}
                          <input type="number" className="mt-1 w-full rounded border px-2 py-1.5 text-sm" value={selectedField[key] ?? 0} onChange={(e) => updateFieldLocal(selectedField.id, { [key]: Number(e.target.value) })} />
                        </label>
                      ))}
                    </div>
                    <label className="inline-flex min-h-[34px] items-center gap-2 text-sm font-semibold text-slate-700">
                      <input type="checkbox" className="size-4" checked={Boolean(selectedField.useTemplateStyle)} onChange={(e) => updateFieldLocal(selectedField.id, { useTemplateStyle: e.target.checked })} />
                      템플릿 기본 스타일 사용
                    </label>
                    <div className="grid grid-cols-2 gap-2">
                      <label className="block text-xs font-semibold text-slate-600">
                        폰트
                        <select className="mt-1 w-full rounded border px-2 py-1.5 text-sm disabled:bg-slate-50" value={selectedFieldStyle.fontFamily} onChange={(e) => updateFieldFontFamily(selectedField.id, e.target.value)} disabled={Boolean(selectedField.useTemplateStyle)}>
                          {availableFontOptions.map((option) => (
                            <option key={fontOptionKey(option)} value={fontOptionKey(option)}>{fontOptionLabel(option)}</option>
                          ))}
                        </select>
                      </label>
                      <label className="block text-xs font-semibold text-slate-600">
                        크기
                        <input type="number" className="mt-1 w-full rounded border px-2 py-1.5 text-sm disabled:bg-slate-50" value={selectedFieldStyle.fontSize || 24} onChange={(e) => updateFieldLocal(selectedField.id, { fontSize: Number(e.target.value) })} disabled={Boolean(selectedField.useTemplateStyle)} />
                      </label>
                      <label className="block text-xs font-semibold text-slate-600">
                        굵기
                        <select className="mt-1 w-full rounded border px-2 py-1.5 text-sm disabled:bg-slate-50" value={selectedFieldStyle.fontWeight || '700'} onChange={(e) => updateFieldLocal(selectedField.id, { fontWeight: e.target.value })} disabled={Boolean(selectedField.useTemplateStyle)}>
                          {selectedFieldFontWeightOptions.map((weight) => (
                            <option key={weight} value={weight}>{fontWeightLabel(weight)}</option>
                          ))}
                        </select>
                      </label>
                      <label className="block text-xs font-semibold text-slate-600">
                        색상
                        <div className="mt-1 flex rounded border bg-white">
                          <input type="color" className="h-[34px] w-10 border-r bg-white p-1 disabled:bg-slate-50" value={selectedFieldStyle.textColor || '#111827'} onChange={(e) => updateFieldLocal(selectedField.id, { textColor: e.target.value })} disabled={Boolean(selectedField.useTemplateStyle)} />
                          <input className="min-w-0 flex-1 px-2 py-1.5 text-sm disabled:bg-slate-50" value={selectedFieldStyle.textColor || '#111827'} onChange={(e) => updateFieldLocal(selectedField.id, { textColor: e.target.value })} disabled={Boolean(selectedField.useTemplateStyle)} />
                        </div>
                      </label>
                      <label className="block text-xs font-semibold text-slate-600">
                        정렬
                        <select className="mt-1 w-full rounded border px-2 py-1.5 text-sm disabled:bg-slate-50" value={selectedFieldStyle.textAlign || 'LEFT'} onChange={(e) => updateFieldLocal(selectedField.id, { textAlign: e.target.value })} disabled={Boolean(selectedField.useTemplateStyle)}>
                          <option value="LEFT">왼쪽</option>
                          <option value="CENTER">가운데</option>
                          <option value="RIGHT">오른쪽</option>
                        </select>
                      </label>
                      <label className="block text-xs font-semibold text-slate-600">
                        줄간격
                        <input type="number" step="0.05" className="mt-1 w-full rounded border px-2 py-1.5 text-sm disabled:bg-slate-50" value={selectedFieldStyle.lineHeight || 1.2} onChange={(e) => updateFieldLocal(selectedField.id, { lineHeight: Number(e.target.value) })} disabled={Boolean(selectedField.useTemplateStyle)} />
                      </label>
                      <label className="block text-xs font-semibold text-slate-600">
                        순서
                        <input type="number" className="mt-1 w-full rounded border px-2 py-1.5 text-sm" value={selectedField.zIndex || 0} onChange={(e) => updateFieldLocal(selectedField.id, { zIndex: Number(e.target.value) })} />
                      </label>
                    </div>
                    <label className="block text-sm">
                      <span className="mb-1 block font-semibold text-slate-700">샘플값</span>
                      <input className="w-full rounded border px-3 py-2" value={selectedField.sampleValue || ''} onChange={(e) => updateFieldLocal(selectedField.id, { sampleValue: e.target.value })} />
                    </label>
                    <button type="button" className="w-full rounded bg-brand-navy px-3 py-2 text-sm font-semibold text-white" onClick={() => saveField(selectedField)}>
                      필드 저장
                    </button>
                  </div>
                ) : (
                  <div className="rounded border border-dashed px-3 py-8 text-center text-sm text-slate-500">선택된 필드가 없습니다</div>
                )}
              </div>
            </div>
          </section>
        </main>
      </div>
    </div>
  )
}
